// Single source of truth for Pharos vocabularies. Tokens are byte-identical to
// the backend allowlists (vault/90-Active/PLAN-pharos-v2.md "Enums"); only the
// labels are frontend-owned. Pharos has no free text: every listing field is
// one of these tokens or a number/date.
export const VEHICLES = [
  { value: "fund", label: "Fund" },
  { value: "spv", label: "SPV" },
  { value: "direct", label: "Direct" },
  { value: "co_invest", label: "Co-invest" },
];

export const ASSET_CLASSES = [
  { value: "vc", label: "Venture" },
  { value: "pe", label: "Private Equity" },
  { value: "growth", label: "Growth" },
  { value: "re", label: "Real Estate" },
  { value: "credit", label: "Credit" },
  { value: "infra", label: "Infrastructure" },
];

export const SECTORS = [
  { value: "software", label: "Software" },
  { value: "fintech", label: "Fintech" },
  { value: "healthcare", label: "Healthcare" },
  { value: "biotech", label: "Biotech" },
  { value: "consumer", label: "Consumer" },
  { value: "industrials", label: "Industrials" },
  { value: "energy", label: "Energy" },
  { value: "climate", label: "Climate" },
  { value: "real_estate", label: "Real estate" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "financials", label: "Financials" },
  { value: "media", label: "Media" },
  { value: "defense", label: "Defense" },
  { value: "deep_tech", label: "Deep tech" },
  { value: "other", label: "Other" },
];

export const REGIONS = [
  { value: "nordics", label: "Nordics" },
  { value: "uk_ireland", label: "UK & Ireland" },
  { value: "western_europe", label: "Western Europe" },
  { value: "dach", label: "DACH" },
  { value: "southern_europe", label: "Southern Europe" },
  { value: "cee", label: "Central & Eastern Europe" },
  { value: "israel", label: "Israel" },
  { value: "mena", label: "MENA" },
  { value: "north_america", label: "North America" },
  { value: "latam", label: "Latin America" },
  { value: "apac", label: "Asia-Pacific" },
  { value: "global", label: "Global" },
];

export const CURRENCIES = ["USD", "EUR", "GBP", "CHF", "ILS"].map((c) => ({ value: c, label: c }));

export const SIZE_BANDS = [
  { value: "lt_10m", label: "< 10M" },
  { value: "m10_50", label: "10M - 50M" },
  { value: "m50_250", label: "50M - 250M" },
  { value: "m250_1b", label: "250M - 1B" },
  { value: "b1_plus", label: "1B+" },
];

export const TICKET_BANDS = [
  { value: "lt_250k", label: "< 250k" },
  { value: "k250_1m", label: "250k - 1M" },
  { value: "m1_5", label: "1M - 5M" },
  { value: "m5_20", label: "5M - 20M" },
  { value: "m20_plus", label: "20M+" },
];

export const HIGHLIGHTS = [
  { value: "lead_investor_committed", label: "Lead investor committed" },
  { value: "top_quartile_manager", label: "Top-quartile manager" },
  { value: "gp_commit_5pct_plus", label: "GP commit 5%+" },
  { value: "oversubscribed", label: "Oversubscribed" },
  { value: "repeat_sponsor", label: "Repeat sponsor" },
  { value: "revenue_positive", label: "Revenue positive" },
  { value: "profitable", label: "Profitable" },
  { value: "secondary_discount", label: "Secondary at a discount" },
  { value: "institutional_coinvestors", label: "Institutional co-investors" },
];

export const MAX_SECTORS = 5;
export const MAX_HIGHLIGHTS = 3;
export const MAX_TRACK_ROWS = 10;

// Owner-facing listing status chips.
export const STATUS_LABELS = {
  pending_review: "Pending review",
  published: "Published",
  closed: "Closed",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
};

// Display label for a token; "" for anything outside the allowlist (no
// raw-value fallback, so an unexpected API value never renders as text).
export function labelFor(list, value) {
  const match = list.find((item) => item.value === value);
  return match ? match.label : "";
}

// ISO 3166-1 alpha-2 allowlist; identical to routes.pharos.PHAROS_COUNTRIES (tested).
export const COUNTRIES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX",
  "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ",
  "BR", "BS", "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK",
  "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM",
  "DO", "DZ", "EC", "EE", "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR",
  "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS",
  "GT", "GU", "GW", "GY", "HK", "HM", "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN",
  "IO", "IQ", "IR", "IS", "IT", "JE", "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN",
  "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV",
  "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ",
  "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA", "NC", "NE", "NF", "NG", "NI",
  "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM",
  "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW", "SA", "SB", "SC",
  "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV",
  "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR",
  "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];
export const MAX_COUNTRIES = 10;

let _regionNames = null;
function regionNames() {
  if (_regionNames === null) {
    try {
      _regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch (e) {
      _regionNames = false;
    }
  }
  return _regionNames;
}

// English display name for an allowlisted ISO alpha-2 code; "" for anything
// outside the allowlist (same no-raw-fallback rule as labelFor).
export function countryName(code) {
  if (!COUNTRIES.includes(code)) return "";
  const names = regionNames();
  let name = "";
  try {
    name = names ? names.of(code) : "";
  } catch (e) {
    name = "";
  }
  return name && name !== code ? name : code;
}
